import React from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { makeStyles } from '@material-ui/core/styles';
import { courseCardFields } from './helpers';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 480,
    maxWidth: '94vw',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    color: '#263548',
  },
  header: {
    padding: theme.spacing(2, 2, 2, 2.5),
    borderBottom: '1px solid #e7ebf0',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: '1.0625rem',
    fontWeight: 700,
    lineHeight: 1.35,
  },
  meta: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f5f8fc',
    color: '#647184',
    border: '1px solid #e7ebf0',
    padding: '3px 8px',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 12,
  },
  issuerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    padding: '4px 10px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.4,
  },
  levelOutline: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: 'transparent',
    color: '#647184',
    border: '1px solid #d5dde8',
    padding: '3px 10px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.4,
  },
  factRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    color: '#647184',
    fontSize: 13,
    fontWeight: 500,
  },
  fact: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  factIcon: {
    fontSize: 16,
    color: '#94a3b8',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2, 2.5),
  },
  section: {
    marginBottom: theme.spacing(2.5),
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#647184',
    marginBottom: theme.spacing(0.75),
  },
  body: {
    fontSize: '0.875rem',
    lineHeight: 1.55,
    color: '#263548',
    whiteSpace: 'pre-wrap',
  },
  list: {
    margin: 0,
    paddingLeft: theme.spacing(2.25),
    '& li': {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#263548',
      marginBottom: 4,
    },
  },
  footer: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: '1px solid #e7ebf0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  actionBtn: {
    borderRadius: '999px !important',
    minHeight: 36,
    padding: '0 16px',
    textTransform: 'none',
    fontWeight: 650,
    letterSpacing: '0.01em',
    boxShadow: 'none !important',
  },
}));

const DetailSection = ({ title, children }) => {
  const classes = useStyles();
  if (!children) {
    return null;
  }
  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle} component="h3">{title}</Typography>
      {children}
    </div>
  );
};

const stringList = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => (typeof item === 'string' ? item : item?.name || item?.label || '')).filter(Boolean);
};

const CourseDetailsDrawer = ({
  course,
  open,
  onClose,
  actionLabel,
  actionClassName,
  actionLoading,
  onAction,
}) => {
  const classes = useStyles();

  if (!course) {
    return (
      <Drawer anchor="right" open={false} onClose={onClose} classes={{ paper: classes.paper }} />
    );
  }

  const skills = stringList(course.skills);
  const outcomes = stringList(course.outcomes);
  const topics = stringList(course.topics);
  const card = courseCardFields(course);
  const description = String(course.description || course.summary || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const prerequisites = course.prerequisites
    ? (Array.isArray(course.prerequisites) ? stringList(course.prerequisites).join('\n') : String(course.prerequisites).trim())
    : '';
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      aria-labelledby="learning-hub-course-details-title"
    >
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <Typography id="learning-hub-course-details-title" component="h2" className={classes.title}>
            {course.name}
          </Typography>
          <IconButton size="small" aria-label="Close details" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.meta}>
          {card.certificateIssuers.map((issuer) => (
            <span key={issuer} className={classes.issuerChip}>{issuer}</span>
          ))}
          {card.level ? <span className={classes.levelOutline}>{card.level}</span> : null}
          {card.duration || card.audience ? (
            <span className={classes.factRow}>
              {card.duration ? (
                <span className={classes.fact}>
                  <AccessTimeIcon className={classes.factIcon} />
                  {card.duration}
                </span>
              ) : null}
              {card.audience ? (
                <span className={classes.fact}>
                  <PersonOutlineIcon className={classes.factIcon} />
                  {card.audience}
                </span>
              ) : null}
            </span>
          ) : null}
          {card.isNew ? <span className={classes.chip}>New</span> : null}
        </div>
      </div>
      <div className={classes.content}>
        <DetailSection title="Description">
          {description ? <Typography className={classes.body}>{description}</Typography> : null}
        </DetailSection>
        <DetailSection title="Skills">
          {skills.length ? (
            <ul className={classes.list}>
              {skills.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : null}
        </DetailSection>
        <DetailSection title="Outcomes">
          {outcomes.length ? (
            <ul className={classes.list}>
              {outcomes.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : null}
        </DetailSection>
        <DetailSection title="Audience">
          {card.audience ? <Typography className={classes.body}>{card.audience}</Typography> : null}
        </DetailSection>
        <DetailSection title="Topics">
          {topics.length ? (
            <ul className={classes.list}>
              {topics.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : null}
        </DetailSection>
        <DetailSection title="Prerequisites">
          {prerequisites ? <Typography className={classes.body}>{prerequisites}</Typography> : null}
        </DetailSection>
      </div>
      <div className={classes.footer}>
        <Button className={classes.actionBtn} onClick={onClose}>
          Close
        </Button>
        {onAction ? (
          <Button
            className={`${classes.actionBtn} ${actionClassName || ''}`.trim()}
            variant="contained"
            disableElevation
            disabled={actionLoading}
            onClick={() => onAction(course)}
          >
            {actionLoading ? 'Please wait...' : actionLabel}
          </Button>
        ) : null}
      </div>
    </Drawer>
  );
};

export default CourseDetailsDrawer;
